// Plain words for the backbone's vocabulary (S-D signed language, 2026-09-07).
// Working screens never show a radius number, a case id, a command name or a
// table name. They show what it means to the person deciding.
import type { Case } from "@artemis-group/aiw-contracts";

export type CaseKind = "a note" | "our records" | "money" | "reaches a customer" | "reaches a supplier" | "reaches an installer" | "reaches a person";

const BY_COMMAND: Record<string, CaseKind> = {
  add_touch: "our records",
  save_communication: "our records",
  send_communication: "reaches a customer",
  schedule_follow_up: "our records",
  send_email: "reaches a customer",
  send_sms: "reaches a customer",
  offer_job: "reaches an installer",
  update_schedule: "reaches an installer",
  schedule_install: "reaches an installer",
  set_install_schedule: "reaches an installer",
  send_po: "reaches a supplier",
  draft_po: "money",
  record_spend: "money",
  create_quote: "money",
};

/** The one chip every case carries. */
export function caseKind(k: Pick<Case, "command" | "radius">): CaseKind {
  const byCmd = BY_COMMAND[k.command];
  if (byCmd) return byCmd;
  switch (k.radius) {
    case 0:
    case 1:
      return "a note";
    case 2:
      return "our records";
    case 3:
      return "money";
    default:
      return "reaches a person";
  }
}

export function undoWords(kind: Case["questions"]["undo"]["kind"]): string {
  switch (kind) {
    case "reversible":
      return "Can be undone";
    case "with_effort":
      return "Undo takes effort";
    default:
      return "Cannot be undone";
  }
}

/** The desk EmmA was acting as, in words ("lead-desk" → "Lead desk"). */
export function deskName(actorId: string): string {
  if (actorId === "emma") return "EmmA";
  const s = actorId.replace(/[-_]+/g, " ").trim().replace(/\bpo\b/gi, "PO");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** A one-line question for the card, the inbox row and the notification.
 *  Derived from the artefact until desks emit a headline of their own. */
export function caseHeadline(k: Case): string {
  const w = k.questions.what;
  if (k.command === "add_touch" && w.kind === "text") {
    return `Record past activity: ${w.body.replace(/^(Outbound|Inbound) [a-z_]+: /, "")}`;
  }
  if(k.command==='create_job'&&w.kind==='record')return w.fields.existing_job ? 'Return the original job for this accepted offer?' : `Create a job for ${String(w.fields.customer??'this customer')}?`;
  if(k.command==='reopen_job'&&w.kind==='record')return `Restart ${String(w.fields.name??'this work')} at Pending Schedule?`;
  if(k.command==='restore_job_stage'&&w.kind==='record')return `Return ${String(w.fields.name??w.fields.ref??'this job')} to ${String(w.fields.to??'its previous stage')}?`;
  if(k.command==='return_job_to_opportunity'&&w.kind==='record')return `Return ${String(w.fields.name??w.fields.ref??'this job')} to the Pipeline as ${String(w.fields.to??'an open enquiry')}?`;
  if(k.command==='link_lead_work'&&w.kind==='record')return `Link ${String(w.fields.customer??'this customer')} to ${String(w.fields.work??'existing work')}?`;
  if(k.command==='update_contact'&&w.kind==='record')return `Update ${String(w.fields.customer??w.fields.name??'customer details')}?`;
  switch (w.kind) {
    case "email":
      return `Send "${w.subject}" to ${w.to.join(", ")}?`;
    case "record": {
      const table = w.table.split(".").pop() ?? w.table;
      return `Record ${table.replace(/_/g, " ")}: ${summariseFields(w.fields)}?`;
    }
    default: {
      const text = w.body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      return text.length > 110 ? `${text.slice(0, 107)}…` : text;
    }
  }
}

function summariseFields(fields: Record<string, unknown>): string {
  const parts = Object.entries(fields)
    .filter(([k, v]) => !/(^id$|_id$|_ref$|^expected_)/.test(k) && v != null && v !== "")
    .slice(0, 3)
    .map(([k, v]) => `${k.replace(/_/g, " ")} ${String(v)}`);
  return parts.join(", ") || "one row";
}

export function confidenceWords(c: number): string {
  if (c >= 0.85) return "sure";
  if (c >= 0.65) return "fairly sure";
  if (c >= 0.45) return "unsure";
  return "guessing";
}

export const aud = (n: number) => n.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits:0,maximumFractionDigits:2 });

/** External effects must stay visible even in compact approval lists. */
export function isExternalCase(k: Case): boolean {
  return caseKind(k).startsWith("reaches ") || k.questions.touches.recipients.length > 0;
}
